/*
 * Copyright (c) 2020. FINN Sp. z o.o.
 */

package pl.finn.forms.tw17

import io.kvision.form.FormType
import io.kvision.form.check.CheckBox
import io.kvision.form.text.ImaskOptions
import io.kvision.form.text.PatternMask
import io.kvision.form.text.Text
import io.kvision.html.*
import io.kvision.panel.SimplePanel
import io.kvision.panel.TabPosition
import io.kvision.panel.tab
import io.kvision.panel.tabPanel
import io.kvision.state.bind
import io.kvision.utils.px
import pl.finn.forms.tw17.Tw17Model.formularzStore
import pl.finn.forms.utils.Components
import pl.finn.forms.utils.FormReadingUtils
import pl.finn.forms.utils.form.FormComponents17
import pl.finn.forms.utils.form.extendedPanel
import pl.finn.forms.utils.model.Addresses17
import pl.finn.forms.utils.model.Person17
import pl.finn.forms.tw1.DocumentTreeNode

class Tw17Panel : SimplePanel() {

  private val formComponents = FormComponents17()

  val TW1_7_13742 = "http://crd.gov.pl/wzor/2025/04/17/13742/"

  init {
    bind(formularzStore) { state ->
      main {
        margin = 15.px
        id = "peupcontent"
        state.bladZapisu?.let {
          div(it, className = "alert alert-danger") {
            role = "alert"
          }
        }
        when (state.widok) {
          FormularzWidok.EDYCJA, FormularzWidok.NOWY -> {
            if (state.form != null) {
              extendedPanel<DocumentTreeNode>(
                type = FormType.INLINE, className = "mx-2 mx-md-5 mt-3"
              ) {
                val components = Components(this)
                val address = Addresses17(this)
                val person = Person17(this)
                add(formComponents.createButtonPanel(state, this@extendedPanel))
                h1(
                  "Tytuł wykonawczy stosowany w egzekucji należności pieniężnych",
                  className = "h4 text-center"
                ) {
                  tabindex = -1
                }
                tabPanel(TabPosition.TOP, className = "nav-pills") {
                  id = "pills-tabs"
                  tab("Dane referencyjne") {
                    setAttribute("data-finn-toggle", "pill")
                    add(
                      components.createSelect(
                        state.form.kodUrzedu,
                        "Kod organu egzekucyjnego (tożsamy z kodem Urzędu Skarbowego)",
                        Tw17Data.listOfTaxOffices,
                        true
                      ).field
                    )
                    add(
                      components.createInput(
                        state.form.sygnatura,
                        "Numer tytułu wykonawczego",
                        "Należy zawsze wpisać numer pierwotnego tytułu wykonawczego"
                      ).field
                    )
                    add(
                      components.createDateTime(
                        state.form.dataWystawienia,
                        "Data wystawienia tytułu wykonawczego",
                        "Należy wskazać datę wystawienia tytułu wykonawczego w zależności od wybranego rodzaju dokumentu:<br/> • pierwotny TW => data wystawienia TW,<br/> • zmieniony TW => data sporządzania zmienionego TW,<br/> • dalszy TW => data wystawienia dotychczasowego TW,<br/> • ponowny TW => data wystawienia dotychczasowego TW,<br/>"
                      ).field
                    )
                    add(
                      components.createSelect(
                        state.form.rodzajDokumentu,
                        "Rodzaj dokumentu",
                        Tw17Data.listOfTitles
                      ).field
                    )

                    val reEgzekucjaElements = ArrayList<Any>()
                    reEgzekucjaElements.add(
                      components.createInput(
                        state.form.numerZarzadzaniaZabezpieczenia,
                        "Numer zarządzania zabezpieczenia"
                      )
                    )
                    reEgzekucjaElements.add(
                      components.createDateTime(
                        state.form.dataZarzadzaniaZabezpieczenia,
                        "Data wydania zarządzania zabezpieczenia"
                      )
                    )
                    val collapsedTwDiv = Div(className = "collapse") { id = "collapseFinnTw" }
                    val withoutDistortion = components.createCollapseCheckbox(
                      state.form.bezPrzeksztalcenia,
                      "Tytuł powstał w wyniku przekształcenia postępowania zabezpieczającego w egzekucyjne",
                      "collapseFinnTw",
                      reEgzekucjaElements,
                      collapsedTwDiv
                    ).field
                    add(withoutDistortion)
                    components.readComponentsList(reEgzekucjaElements, collapsedTwDiv)
                    components.changeElementChildrenStatus(reEgzekucjaElements, (withoutDistortion as CheckBox).value)
                    add(collapsedTwDiv)

                    val reZarzadzenieZabezpieczenieElements = ArrayList<Any>()
                    reZarzadzenieZabezpieczenieElements.add(
                      components.createInput(
                        state.form.numerRefZarzadzenieZabezpieczenia,
                        "Numer referencyjny zarządzenia zabezpieczenia"
                      )
                    )
                    val collapsedZarzadzenieZabezpieczenieDiv = Div(className = "collapse") { id = "collapseFinnZarzadzenieZabezpieczenie" }
                    val withoutDistortionZarzadzenieZabezpieczenie = components.createCollapseCheckbox(
                      state.form.zarzadzenieZabezpieczenia,
                      "Zarządzenie zabezpieczeń zostało przekazane za pośrednictwem systemu eTW",
                      "collapseFinnZarzadzenieZabezpieczenie",
                      reZarzadzenieZabezpieczenieElements,
                      collapsedZarzadzenieZabezpieczenieDiv
                    ).field
                    add(withoutDistortionZarzadzenieZabezpieczenie)
                    components.readComponentsList(reZarzadzenieZabezpieczenieElements, collapsedZarzadzenieZabezpieczenieDiv)
                    components.changeElementChildrenStatus(reZarzadzenieZabezpieczenieElements, (withoutDistortionZarzadzenieZabezpieczenie as CheckBox).value)
                    add(collapsedZarzadzenieZabezpieczenieDiv)

                    val nestedElements = ArrayList<Any>()
                    nestedElements.add(
                      components.createInput(
                        state.form.numerPorzadkowy,
                        "Numer porządkowy dalszego tytułu wykonawczego",
                        requirements = FormReadingUtils.checkValidation("positiveInteger")
                      )
                    )
                    nestedElements.add(
                      components.createSelect(
                        state.form.celWydania,
                        "Cel wydania dalszego tytułu wykonawczego",
                        Tw17Data.listOfAims
                      )
                    )
                    nestedElements.add(
                      components.createDateTime(
                        state.form.dataWydaniaDalszegoTW,
                        "Data wydania dalszego tytułu wykonawczego"
                      )
                    )
                    nestedElements.add(
                      components.createSelect(
                        state.form.dotyczyDokumentu,
                        "Dalszy tytuł wykonawczy dotyczy",
                        Tw17Data.listOfRelates
                      )
                    )
                    nestedElements.add(
                      components.createInput(
                        state.form.kosztyEgzekucyjne,
                        "Koszty egzekucyjne",
                        requirements = FormReadingUtils.checkValidation("TKwota2Nieujemna"),
                        isRequired = false
                      )
                    )
                    val collapsedDtwDiv = Div(className = "collapse") { id = "collapseFinnDtw" }
                    val collapseExecutive = components.createCollapseCheckbox(
                      state.form.dtw,
                      "Dalszy tytuł wykonawczy",
                      "collapseFinnDtw",
                      nestedElements,
                      collapsedDtwDiv
                    ).field
                    add(collapseExecutive)
                    components.readComponentsList(nestedElements, collapsedDtwDiv)
                    components.changeElementChildrenStatus(nestedElements, (collapseExecutive as CheckBox).value)
                    add(collapsedDtwDiv)
                    val reAnnotationElements = ArrayList<Any>()
                    reAnnotationElements.add(
                      components.createDateTime(
                        state.form.dataWystawieniaPonownegoTW,
                        "Tytuł wykonawczy / zmieniony tytuł wykonawczy został ponownie wydany w dniu"
                      )
                    )
                    reAnnotationElements.add(
                      components.createInput(
                        state.form.nazwaWierzyciela,
                        "w związku z postanowieniem"
                      )
                    )
                    reAnnotationElements.add(
                      components.createInput(
                        state.form.sygnaturaPostanowienia,
                        "numer (sygnatura) postanowienia wierzyciela"
                      )
                    )
                    reAnnotationElements.add(
                      components.createDateTime(
                        state.form.dataWydaniaPostanowienia,
                        "z dnia"
                      )
                    )
                    reAnnotationElements.add(
                      components.createSelect(
                        state.form.rodzajUtraconegoDokumentu,
                        "o utracie",
                        Tw17Data.listOfRelates
                      )
                    )
                    val collapsedReAnnotationDiv = Div(className = "collapse") { id = "collapseFinnAnnotation" }
                    val collapseAnnotation = components.createCollapseCheckbox(
                      state.form.adnotacja,
                      "Adnotacja dotycząca ponownie wydanego tytułu wykonawczego",
                      "collapseFinnAnnotation",
                      reAnnotationElements,
                      collapsedReAnnotationDiv
                    ).field
                    add(collapseAnnotation)
                    components.readComponentsList(reAnnotationElements, collapsedReAnnotationDiv)
                    components.changeElementChildrenStatus(reAnnotationElements, (collapseAnnotation as CheckBox).value)
                    add(collapsedReAnnotationDiv)

                    val rePierwszyDokumentElements = ArrayList<Any>()
                    rePierwszyDokumentElements.add(
                      components.createInput(
                        state.form.numerRefPierwszegoDokumentu,
                        "Numer referencyjny pierwszego dokumentu"
                      )
                    )
                    rePierwszyDokumentElements.add(
                      components.createSelect(
                        state.form.rodzajPierwszegoDokumentu,
                        "Rodzaj pierwszego dokumentu",
                        Tw17Data.listOfRelatesPierwszyDokument,
                        isRequired = false
                      )
                    )

                    val pierwszyMap = HashMap<String, Any?>()
                    pierwszyMap["2"] = rePierwszyDokumentElements

                    val radioPierwszy = components.createRadioGroup(
                      state.form.pierwszyDokumentWSprawieRadio,
                      "Pierwszy dokument w sprawie został przekazany za pośrednictwem systemu eTW",
                      Tw17Data.optionsOfTakNie,
                      pierwszyMap,
                      isRequired = false
                    )
                    add(radioPierwszy.field)
                    components.readComponentsList(pierwszyMap, this, radioPierwszy)
                  }
                  tab("Zobowiązany") {
                    setAttribute("data-finn-toggle", "pill")
                    val obligatedMap = HashMap<String, Any?>()
                    val naturalPerson =
                      person.createNaturalPerson(state.form.zobowiazanyOsobaFizyczna, "Long", "Osoba fizyczna")
                    val obligatedPhysical = person.createdObligatedPersons(
                      state.form.zobowiazanyOsobaFizycznaZobowiazanyPierwotny,
                      state.form.zobowiazanyOsobaFizycznaZobowiazanyPierwotnyData
                    )
                    val obligatedPhysicalCollapse: Div = Div()
                    components.readComponentsList(obligatedPhysical, obligatedPhysicalCollapse)
                    (naturalPerson[1] as ArrayList<Any>).add(arrayListOf(obligatedPhysicalCollapse))

                    val nonPhysicalPerson =
                      person.createNonPhysicalPerson(state.form.zobowiazanyOsobaNiefizyczna, "Long", "Osoba prawna", true)
                    val obligated = person.createdObligatedPersons(
                      state.form.zobowiazanyOsobaNiefizycznaZobowiazanyPierwotny,
                      state.form.zobowiazanyOsobaNiefizycznaZobowiazanyPierwotnyData
                    )
//                    val partnersCollapse: Div = partners[1] as Div
                    val obligatedCollapse: Div = Div()
//                    components.readComponentsList(partners[2], partnersCollapse)
//                    components.readComponentsList(partners[3], partnersCollapse)
                    components.readComponentsList(obligated, obligatedCollapse)
//                    (nonPhysicalPerson[1] as ArrayList<Any>).add(partners[0])
//                    (nonPhysicalPerson[1] as ArrayList<Any>).add(arrayListOf(partnersCollapse))
                    (nonPhysicalPerson[1] as ArrayList<Any>).add(arrayListOf(obligatedCollapse))

                    val spouses = ArrayList<Any>()
                    spouses.add(
                      components.createFieldset(
                        "Małżonkowie odpowiedzialni solidarnie"
                      )
                    )
                    val spousesArray = ArrayList<Any>()
                    spousesArray.add(
                      person.createNaturalPerson(
                        state.form.zobowiazanyMalzonkowie[0],
                        "Long",
                        "Zestaw danych identyfikacyjnych małżonka solidarnego",
                        false
                      )
                    )
                    spousesArray.add(
                      person.createNaturalPerson(
                        state.form.zobowiazanyMalzonkowie[1],
                        "Long",
                        "Zestaw danych identyfikacyjnych małżonka solidarnego",
                        false
                      )
                    )

                    val obligatedSpouses = person.createdObligatedPersons(
                      state.form.zobowiazanyMalzonekZobowiazanyPierwotny,
                      state.form.zobowiazanyMalzonekZobowiazanyPierwotnyData
                    )
                    val obligatedSpousesCollapse: Div = Div()
                    components.readComponentsList(obligatedSpouses, obligatedSpousesCollapse)
                    spousesArray.add(
                      components.createSelect(
                        state.form.zobowiazanyMalzonkowieOdpowiedzialnosc,
                        "Rodzaj odpowiedzialności zobowiązanego w stosunku do wskazanego podmiotu, u którego wystąpiło zobowiązanie",
                        Tw17Data.listOfResponsibilityTypes
                      )
                    )
                    spousesArray.add(arrayListOf(obligatedSpousesCollapse))
                    spouses.add(spousesArray)

                    val nonPhysicalJednostka =
                      person.createNonPhysicalPerson(state.form.zobowiazanyJednostka, "Long", "Jednostka organizacyjna niebędąca osobą prawną", true)
                    val partnersJednostka = person.createPartners(
                      state.form.zobowiazanyJednostkaWspolnicy,
                      state.form.zobowiazanyJednostkaWspolnicyData
                    )
                    val obligatedJednostka = person.createdObligatedPersons(
                      state.form.zobowiazanyJednostkaZobowiazanyPierwotny,
                      state.form.zobowiazanyJednostkaZobowiazanyPierwotnyData
                    )
                    val partnersJednostkaCollapse: Div = partnersJednostka[1] as Div
                    val obligatedJednostkaCollapse: Div = Div()
                    components.readComponentsList(obligatedJednostka, obligatedJednostkaCollapse)
                    (nonPhysicalJednostka[1] as ArrayList<Any>).add(partnersJednostka[0])
                    (nonPhysicalJednostka[1] as ArrayList<Any>).add(arrayListOf(partnersJednostkaCollapse))
                    (nonPhysicalJednostka[1] as ArrayList<Any>).add(arrayListOf(obligatedJednostkaCollapse))

                    val nonPhysicalPodmiot =
                      person.createNonPhysicalPodmiot(state.form.zobowiazanyPodmiot, "Long")
                    val obligatedPodmiot = person.createdObligatedPersons(
                      state.form.zobowiazanyPodmiotZobowiazanyPierwotny,
                      state.form.zobowiazanyPodmiotZobowiazanyPierwotnyData
                    )
//                    val partnersPodmiotCollapse: Div = partnersPodmiot[1] as Div
                    val obligatedPodmiotCollapse: Div = Div()
                    components.readComponentsList(obligatedPodmiot, obligatedPodmiotCollapse)
//                    (nonPhysicalPodmiot[1] as ArrayList<Any>).add(partnersPodmiot[0])
//                    (nonPhysicalPodmiot[1] as ArrayList<Any>).add(arrayListOf(partnersPodmiotCollapse))
                    (nonPhysicalPodmiot[1] as ArrayList<Any>).add(arrayListOf(obligatedPodmiotCollapse))

                    val nonPhysicalPrzedsiebiorstwoWSpadku =
                      person.createNonPhysicalPrzedsiebiorstwoWSpadku(state.form.zobowiazanyPrzedsiebiorstwoWSpadku, "Long")

                    obligatedMap["0"] = naturalPerson
                    obligatedMap["1"] = spouses
                    obligatedMap["2"] = nonPhysicalPerson
                    obligatedMap["3"] = nonPhysicalJednostka
                    obligatedMap["4"] = nonPhysicalPodmiot
                    obligatedMap["5"] = nonPhysicalPrzedsiebiorstwoWSpadku

                    val radio = components.createRadioGroup(
                      state.form.zobowiazany,
                      "Dane zobowiązanego/zobowiązanych",
                      Tw17Data.optionsOfObligated,
                      obligatedMap
                    )

                    add(radio.field)
                    components.readComponentsList(obligatedMap, this, radio)
                  }
                  tab("Należność") {

                    setAttribute("data-finn-toggle", "pill")
                    val remittance = ArrayList<Any>()
                    val remittanceData = ArrayList<Any>()
                    remittance.add(
                      components.createFieldset(
                        "Dane dotyczące należności pieniężnych"
                      )
                    )
                    remittanceData.add(
                      components.createTextArea(
                        state.form.naleznoscAktNormatywny,
                        "Oznaczenie przepisu prawa, z którego wynika należność pieniężna (ze słownika podstaw prawnych)",
                        tooltipMessage = "np. Podatek rolny-art. 6a ustawy z 15.11.1984 o pod. rolnym (t.j. Dz.U.1993.94.431)"
                      )
                    )
                    remittanceData.add(
                      components.createTextArea(
                        state.form.naleznoscRodzajNaleznosci,
                        "Rodzaj należności pieniężnej <br> <a target=\"_blank\" href=\"http://www.is-szczecin.pl/egzadm/podstawyprawne1.php\"><b>Słownik podstaw prawnych</b></a>",
                        tooltipMessage = "np. Podatek rolny"
                      )
                    )

                    val naleznoscRodzajNaleznosciKodPodstawyPrawnejComponent = components.createInput(
                      state.form.naleznoscRodzajNaleznosciKodPodstawyPrawnej,
                      "Kod podstawy prawnej (ze słownika podstaw prawnych)",
                      tooltipMessage = "np. CL 1AK9320090301 (kod może zawierać min. 12 znaków a  max. 30 znaków)",
                      requirements = arrayOf("[a-zA-Z0-9 ]{12,30}", "kod musi zawierać min. 12 znaków a  max. 30 znaków")
                    )

                    (naleznoscRodzajNaleznosciKodPodstawyPrawnejComponent.field as Text).apply {
                      maskOptions = ImaskOptions(
                        pattern = PatternMask(
                          pattern = "a[a]{ }0********[*][*][*][*][*][*][*][*][*][*][*][*][*][*][*][*][*][*][*]",
                          lazy = false,
                          eager = null,
                          placeholderChar = ' ',
                          definitions = null,
                          blocks = null,
                        )
                      )
                    }
                    remittanceData.add(
                      naleznoscRodzajNaleznosciKodPodstawyPrawnejComponent
                    )
                    remittanceData.add(
                      components.createSelect(
                        state.form.naleznoscRodzajNaleznosciRodzaj,
                        "Kategoria należności",
                        Tw17Data.listOfReceivable
                      )
                    )

                    val legalBasis = HashMap<String, Any?>()
                    legalBasis["0"] =
                      components.createInput(
                        state.form.naleznoscObowiazekZMocyPrawa,
                        "Identyfikacja podstawy prawnej obowiązku wynikającego z mocy prawa"
                      )
                    legalBasis["1"] =
                      components.createInput(
                        state.form.naleznoscObowiazekInnyDokument,
                        "Identyfikacja podstawy prawnej obowiązku wynikającego z dokumentu, o którym mowa w art. 3a § 1 ustawy"
                      )
                    legalBasis["2"] = arrayListOf(
                      components.createDateTime(
                        state.form.naleznoscOrzeczenieDataWydania,
                        "Data wydania orzeczenia"
                      ),
                      components.createInput(
                        state.form.naleznoscObowiazekOrzeczenie,
                        "Identyfikacja podstawy prawnej obowiązku wynikającego z orzeczenia"
                      )
                    )
                    val radio = components.createRadioGroup(
                      state.form.naleznoscObowiazek,
                      "Podstawa prawna obowiązku",
                      Tw17Data.optionsOfLegalBasis,
                      legalBasis
                    )

                    remittanceData.add(radio)
                    remittanceData.add(legalBasis)

                    val legalProvision = HashMap<String, Any?>()
                    legalProvision["0"] =
                      components.createInput(
                        state.form.naleznoscDoreczeniaBrakUpomnienia,
                        "Podstawa prawna braku obowiązku doręczenia upomnienia"
                      )
                    val reminder = ArrayList<Any>()
                    val reminderData = ArrayList<Any>()
                    reminder.add(
                      components.createFieldset(
                        "Upomnienie"
                      )
                    )
                    reminderData.add(
                      components.createInput(
                        state.form.naleznoscDoreczeniaKosztyUpomnienia,
                        "Kwota kosztów upomnienia",
                        tooltipMessage = "Wymagana wartość: <b>xx.xx</b>",
                        requirements = FormReadingUtils.checkValidation("TKwota2Nieujemna")
                      )
                    )

                    val reminderDeliveryDataLabel = "Data doręczenia upomnienia"
                    val reminderDeliveryDataList = person.createReminderDeliveryData(state.form.naleznoscDoreczeniaDataDoreczenia, reminderDeliveryDataLabel)
                    val divReminderDeliveryData = Div() { id = "reminderDeliveryData" }
                    reminderData.add(divReminderDeliveryData)
                    reminderData.add(
                      arrayListOf(
                        reminderDeliveryDataList,
                        person.createButtonReminderDeliveryDataGroup(state.form.naleznoscDoreczeniaDataDoreczenia, divReminderDeliveryData, reminderDeliveryDataLabel)
                      )
                    )
                    reminder.add(reminderData)
                    legalProvision["1"] = reminder
                    val notification = ArrayList<Any>()
                    val notificationData = ArrayList<Any>()
                    notification.add(
                      components.createFieldset(
                        "Powiadomienie"
                      )
                    )
                    notification.add(
                      components.createDateTime(
                        state.form.naleznoscPowiadomienieDataDoreczenia,
                        "Data doręczenia powiadomienia"
                      )
                    )
                    notificationData.add(
                      components.createFieldset(
                        "Oznaczenie organu odpowiedzialnego za rozpatrzenie administracyjnej kary pieniężnej lub grzywny administracyjnej"
                      )
                    )
                    notificationData.add(person.createSubject(state.form.namespaceURI, state.form.naleznoscOgranOdpowiedzialny))

                    val collapsedAdditionalInfDiv = Div(className = "collapse") { id = "collapseFinnAdditionalInf" }
                    val additionalInf = person.createSubject(state.form.namespaceURI, state.form.naleznoscOgranUdzielajacyInformacjiData)

                    val collapseNotification = components.createCollapseCheckbox(
                      state.form.naleznoscOgranUdzielajacyInformacji,
                      "Oznaczenie organu, który może udzielić dodatkowych informacji dotyczących administracyjnej kary pieniężnej lub grzywny administracyjnej lub możliwości odwołania się od obowiązku zapłaty lub zaskarżenia decyzji w sprawie ich nałożenia",
                      "collapseFinnAdditionalInf",
                      additionalInf,
                      collapsedAdditionalInfDiv
                    )

                    notificationData.add(collapseNotification)
                    components.changeElementChildrenStatus(
                      additionalInf,
                      (collapseNotification.field as CheckBox).value
                    )
                    notificationData.add(additionalInf)
                    notification.add(notificationData)

                    legalProvision["2"] = notification
                    val radioProvision = components.createRadioGroup(
                      state.form.naleznoscDoreczenia,
                      "Oznaczenie przepisu prawa, z którego wynika należność pieniężna (ze słownika podstaw prawnych)",
                      Tw17Data.optionsOfDelivered,
                      legalProvision
                    )

                    remittanceData.add(radioProvision)
                    remittanceData.add(legalProvision)

                    val withInterest = ArrayList<Any>()
                    val withInterestData = ArrayList<Any>()
                    withInterest.add(
                      components.createFieldset("Pobiera się odsetki we wskazanej stawce")
                    )
//                    withInterestData.add(Label("<a target=\"_blank\" href=\"https://kalkulatory.gofin.pl/Kalkulatory-odsetek,12.html\"><b>Kalkulatory odsetek</b></a>", rich = true))
                    withInterestData.add(
                      components.createSelect(
                        state.form.zobowiazanieOdsetkiRodzaj,
                        "Rodzaj odsetek",
                        Tw17Data.listOfInterest
                      )
                    )
                    withInterestData.add(address.createPositions(state.form.zobowiazanieOdsetkiPozycja, "Pozycja", true))
                    val rateData = HashMap<String, Any?>()
                    rateData["0"] = components.createInput(
                      state.form.zobowiazanieOdsetkiStawka,
                      "Stawka odsetek"
                    )
                    rateData["1"] = components.createInput(
                      state.form.zobowiazanieOdsetkiPodwyzszonaStawka,
                      "Podwyższona stawka odsetek"
                    )
                    val radioRate = components.createRadioGroup(
                      state.form.zobowiazanieOdsetki,
                      "",
                      Tw17Data.optionsOfRates,
                      rateData
                    )
                    withInterestData.add(radioRate)
                    withInterestData.add(rateData)
                    withInterest.add(withInterestData)
                    val obligationData = HashMap<String, Any?>()
                    obligationData["0"] =
                      address.createPositions(state.form.zobowiazanieBrakOdsetekPozycja, "Nie pobiera się odsetek", false)
                    obligationData["1"] = withInterest
                    val radioObligation = components.createRadioGroup(
                      state.form.zobowiazanie,
                      "Zobowiązanie",
                      Tw17Data.optionsOfObligation,
                      obligationData
                    )
                    remittanceData.add(radioObligation)
                    remittanceData.add(obligationData)

                    remittanceData.add(
                      components.createTextArea(
                        state.form.innaPodstawaPrawna,
                        "Pełen tekst innej podstawy prawnej egzekucji",
                        isRequired = false
                      )
                    )
                    remittanceData.add(
                      components.createFieldset(
                        "Informacje dotyczące należności pieniężnej"
                      )
                    )
                    remittanceData.add(
                      arrayListOf(
                        components.createCheckbox(
                          state.form.odpowiedzialnoscObejmujeMajatekWspolny,
                          "odpowiedzialność za należność pieniężną i odsetki z tytułu niezapłacenia jej w terminie obejmuje również majątek wspólny zobowiązanego i jego małżonka",
                          isRequired = false
                        ),
                        components.createCheckbox(
                          state.form.obowiazekScisleZwiazanyZeZobowiazanym,
                          "obowiązek jest ścieśle związany ze zobowiązanym",
                          isRequired = false
                        )
                      )
                    )

                    val nZElements = ArrayList<Any>()
                    nZElements.add(
                      components.createFieldset(
                        "Zabezpieczenie"
                      )
                    )
                    nZElements.add(
                      arrayListOf(
                        components.createSelect(
                          state.form.naleznoscZabezpieczenia.hipotekaZastaw,
                          "Rodzaj zabezpieczenia należności pieniężnej",
                          Tw17Data.listOfCashReceivableCollateral
                        ),
                        components.createDateTime(
                          state.form.naleznoscZabezpieczenia.dataPowstania,
                          "Data powstania zabezpieczenia"
                        )
                      )
                    )
                    val collapsedNZDiv = Div(className = "collapse") { id = "collapseFinnNZ" }
                    val collapsedNZExecutive = components.createCollapseCheckbox(
                      state.form.naleznoscZabezpieczenia.posiadaZabezpieczenie,
                      "Występuje zabezpieczenie należności pieniężnej hipoteką przymusową lub zastawem",
                      "collapseFinnNZ",
                      nZElements,
                      collapsedNZDiv
                    )
                    remittanceData.add(collapsedNZExecutive)
                    components.readComponentsList(nZElements, collapsedNZDiv)
                    components.changeElementChildrenStatus(nZElements, (collapsedNZExecutive.field as CheckBox).value)
                    remittanceData.add(nZElements)

                    remittance.add(remittanceData)
                    components.readComponentsList(remittance, this)
                  }
                  tab("Wierzyciel") {
                    setAttribute("data-finn-toggle", "pill")
                    val creditor = ArrayList<Any>()
                    val creditorData = ArrayList<Any>()
                    creditor.add(
                      components.createFieldset(
                        "Oznaczenie i wniosek wierzyciela"
                      )
                    )
                    creditorData.add(
                      components.createInput(
                        state.form.wierzycielKodWierzyciela,
                        "Kod wierzyciela (ze słownika wierzycieli ustawowych)" +
                            "<br> <a target=\"_blank\" href=\"http://www.is-szczecin.pl/egzadm/wierzyciele1_prev.php\"><b>Słownik wierzycieli</b></a>",
                        tooltipMessage = "np. IAPT01 (kod dla BESKIDZKA OKRĘGOWA IZBA APTEKARSKA)",
                        requirements = FormReadingUtils.checkValidation("TKodWierzyciela")
                      )
                    )
                    creditorData.add(components.createInput(state.form.wierzycielNazwa, "Nazwa wierzyciela"))

                    val identityTypMap = HashMap<String, Any?>()
                    val typData = ArrayList<Any>()

                    identityTypMap["0"] = arrayListOf<Any>(
                      components.createFieldset("Adres siedziby wierzyciela"),
                              address.createPolandAddress(state.form.wierzycielAdres),
                              components.createFieldset("Identyfikator wierzyciela"),
                              arrayListOf<Any>(
                                components.createInput(
                                  state.form.wierzycielNip,
                                  "NIP",
                                  "Identyfikator podatkowy NIP wierzyciela, a jeżeli wierzyciel go nie posiada - urzędu obsługującego wierzyciela lub jednostki samorządu terytorialnego, której organ jest wierzycielem",
                                  isRequired = true,
                                  requirements = FormReadingUtils.checkValidation("TNrNIP")
                                ),
                                components.createInput(
                                  state.form.wierzycielRegon,
                                  "REGON",
                                  "REGON wierzyciela, a jeżeli wierzyciel go nie posiada - urzędu obsługującego wierzyciela lub jednostki samorządu terytorialnego, której organ jest wierzycielem",
                                  isRequired = false,
                                  requirements = FormReadingUtils.checkValidation("TNrREGON")
                                )
                              ),
                              components.createInput(state.form.wierzycielNrTelefonu, "Numer telefonu", isRequired = false, requirements = FormReadingUtils.checkValidation("TNrTelefonu")),
                              components.createInput(state.form.wierzycielNrTelefonuWew, "Numer wewnętrzny", isRequired = false),
                              components.createInput(
                                state.form.wierzycielEmail,
                                "Adres e-mail",
                                requirements = FormReadingUtils.checkValidation("TAdresEmail"),
                                isRequired = false
                              ),
                              components.createInput(
                                state.form.wierzycielAdresEDorenczen,
                                "Adres skrzynki e-Doręczeń",
                                requirements =
                                  FormReadingUtils.checkValidation("TEdoreczenia"),
                                isRequired = false
                              )
                    )
                    identityTypMap["1"] = arrayListOf<Any>(
                      components.createFieldset("Adres siedziby wierzyciela"),
                      address.createPolandAddress(state.form.wierzycielOrganAdres),
                      components.createFieldset("Organ upoważniony do wykonywania praw i obowiązków w imieniu wierzyciela"),
                      components.createInput(
                        state.form.wierzycielOrganNazwa,
                        "Nazwa organu upoważnionego do wykonywania praw i obowiązków w imieniu wierzyciela",
                        isRequired = false
                      ),
                      address.createPolandAddress(state.form.wierzycielOrganAdresPolski),
                      arrayListOf<Any>(
                        components.createInput(
                          state.form.wierzycielOrganNip,
                          "NIP",
                          "Identyfikator podatkowy NIP wierzyciela, a jeżeli wierzyciel go nie posiada - urzędu obsługującego wierzyciela lub jednostki samorządu terytorialnego, której organ jest wierzycielem",
                          isRequired = true,
                          requirements = FormReadingUtils.checkValidation("TNrNIP")
                        ),
                        components.createInput(
                          state.form.wierzycielOrganRegon,
                          "REGON",
                          "REGON wierzyciela, a jeżeli wierzyciel go nie posiada - urzędu obsługującego wierzyciela lub jednostki samorządu terytorialnego, której organ jest wierzycielem",
                          isRequired = false,
                          requirements = FormReadingUtils.checkValidation("TNrREGON")
                        ),
                        components.createInput(state.form.wierzycielOrganNrTelefonu, "Numer telefonu", isRequired = false, requirements = FormReadingUtils.checkValidation("TNrTelefonu")),
                        components.createInput(state.form.wierzycielOrganNrTelefonuWew, "Numer wewnętrzny", isRequired = false),
                        components.createInput(
                          state.form.wierzycielOrganEmail,
                          "Adres e-mail",
                          requirements = FormReadingUtils.checkValidation("TAdresEmail"),
                          isRequired = false
                        ),
                        components.createInput(
                          state.form.wierzycielOrganAdresEDorenczen,
                          "Adres skrzynki e-Doręczeń",
                          requirements =
                            FormReadingUtils.checkValidation("TEdoreczenia"),
                          isRequired = false
                        )
                      ),
                    )

                    typData.add(
                      components.createRadioGroup(
                        state.form.wierzycieWniosekOgezekucjeKieruje,
                        "Wniosek o egzekucje kieruje",
                        Tw17Data.optionsOfWierzyciel,
                        identityTypMap
                      )
                    )
                    typData.add(identityTypMap)
                    creditorData.add(
                      typData
                    )

                    creditorData.add(components.createFieldset("Nazwa, adres siedziby i numer rachunku bankowego podmiotu, któremu należy przekazać wyegzekwowaną należność pieniężną"))

                    val identityRodzajMap = HashMap<String, Any?>()
                    val rodzajData = ArrayList<Any>()

                    identityRodzajMap["0"] = arrayListOf<Any>(
                      person.createNrKonta(state.form.beneficjentNrRachunkuPl)
                    )
                    identityRodzajMap["1"] = arrayListOf<Any>(
                      components.createInput(state.form.beneficjentNazwa, "Nazwa beneficjenta"),
                      components.createFieldset("Adres siedziby beneficjenta"),
                      address.createPolandAddress(state.form.beneficjentAdres),
                      person.createNrKonta(state.form.beneficjentNrRachunkuPlInnyOrgan)
                    )

                    rodzajData.add(
                      components.createRadioGroup(
                        state.form.beneficjentNalezyPrzekazac,
                        "Należność należy przekazać na konto",
                        Tw17Data.optionsOfWierzycielNaleznosc,
                        identityRodzajMap
                      )
                    )
                    rodzajData.add(identityRodzajMap)

                    creditorData.add(
                      arrayListOf<Any>(
                        rodzajData
                      )
                    )

                    creditorData.add(components.createFieldset("Osoba upoważniona do działania w imieniu wierzyciela"))
                    creditorData.add(
                      arrayListOf<Any>(
                        components.createInput(
                          state.form.osobaUpowaznionaImie,
                          "Imię osoby upoważnionej",
                          tooltipMessage = "W polu imię/imiona osoby upoważnionej należy wpisać dokładnie takie same dane, jak w danych certyfikatu."
                        ),
                        components.createInput(
                          state.form.osobaUpowaznionaNazwisko,
                          "Nazwisko osoby upoważnionej"
                        ),
                        components.createInput(
                          state.form.osobaUpowaznionaStanowisko,
                          "Stanowisko służbowe osoby upoważnionej",
                          isRequired = false
                        ),
                      )
                    )
                    creditor.add(creditorData)
                    components.readComponentsList(creditor, this)
                  }
                  tab("Załączniki") {
                    setAttribute("data-finn-toggle", "pill")
                    components.readComponentsList(
                      formComponents.addAttachments(state, this@extendedPanel), this
                    )
                    if (!state.zalaczniki.isNullOrEmpty()) {
                      components.readComponentsList(
                        formComponents.createAttachmentsTable(state, this@extendedPanel), this
                      )
                    }
                    val concersData = HashMap<String, Any?>()
                    val assetsLabel = "posiadanego przez zobowiązanego składnika majątkowego lub źródła jego dochodu"
                    val parents = person.createAnAsset(state.form.skladnikMajatku, assetsLabel)
                    val div = Div() { id = "anAsset" }
                    concersData["0"] = arrayListOf<Any>(
                      components.createFieldset("Składniki majątkowe"),
                      div,
                      arrayListOf(
                        parents,
                        person.createButtonAssetGroup(state.form.skladnikMajatku, div, assetsLabel)
                      )
                    )

                    val enforcementLabel =
                      "majątku zobowiązanego lub większej jego części znajdującego się na terenie działania organu egzekucyjnego"
                    val enforcementAuthority =
                      person.createAnAsset(state.form.majatekZobowiazanegoNaTerenieOE, enforcementLabel)
                    val enforcement = Div() { id = "enforcementAuthority " }
                    concersData["1"] = arrayListOf<Any>(
                      components.createFieldset("Majątku zobowiązanego lub większej jego części znajdującego się na terenie działania organu egzekucyjnego"),
                      enforcement,
                      arrayListOf(
                        enforcementAuthority,
                        person.createButtonAssetGroup(
                          state.form.majatekZobowiazanegoNaTerenieOE,
                          enforcement,
                          enforcementLabel
                        )
                      )
                    )

                    concersData["2"] = arrayListOf<Any>(
                      components.createFieldset("Siedziby lub miejsca zamieszkania dłużnika zobowiązanego, który jest obciążony prawem majątkowym względem zobowiązanego"),
                      arrayListOf(
                        address.createAddress(
                          state.form.adresDluznikaZobowiazanegoAdres,
                          state.form.adresDluznikaZobowiazanegoAdresData
                        ),
                        components.createInput(state.form.adresDluznikaZobowiazanegoNrTelefonu, "Numer telefonu", requirements = FormReadingUtils.checkValidation("TNrTelefonu"), isRequired = false),
                        components.createInput(state.form.adresDluznikaZobowiazanegoNrWew, "Numer wewnętrzny", isRequired = false)
                      )
                    )
                    concersData["3"] = arrayListOf<Any>(
                      components.createFieldset("Data wysłania przez centralne biuro łącznikowe wniosku o udzielenie informacji lub powiadomienie do państwa członkowskiego lub państwa trzeciego oraz numeru referencyjnego tego wniosku"),
                      arrayListOf(
                        components.createDateTime(
                          state.form.dataNumerWnioskuDataWyslania,
                          "Data wysłania wniosku lub powiadomienia"
                        ),
                        components.createInput(state.form.dataNumerWnioskuNrRefWniosku, "Numer referencyjny wniosku")
                      )
                    )
                    concersData["4"] = arrayListOf<Any>(
                      components.createFieldset("Ujawnionego po umorzeniu postępowania egzekucyjnego na podstawie art. 59 § 2 składnika majątkowego lub źródła dochodu zobowiązanego oraz numeru i daty wydania postanowienia o umorzeniu postępowania egzekucyjnego"),
                      arrayListOf(
                        components.createDateTime(
                          state.form.postanowienieUmorzeniaDataWydania,
                          "Data wysłania wniosku lub powiadomienia"
                        ),
                        components.createInput(
                          state.form.postanowienieUmorzeniaNrPostanowienia,
                          "Numer referencyjny wniosku"
                        ),
                        components.createTextArea(state.form.postanowienieUmorzeniaTrescInformacji, "Treść informacji")
                      )
                    )

                    val identityMap = HashMap<String, Any?>()
                    val naturalPersonData = ArrayList<Any>()
                    identityMap["0"] = components.createInput(
                      state.form.zalacznikiMalzonek.nip,
                      "NIP",
                      requirements = FormReadingUtils.checkValidation("TNrNIP")
                    )
                    identityMap["1"] = components.createInput(
                      state.form.zalacznikiMalzonek.pesel,
                      "PESEL",
                      requirements = FormReadingUtils.checkValidation("TNrPESEL")
                    )
                    identityMap["2"] = components.createCheckbox(
                      state.form.zalacznikiMalzonek.oswiadczenieWierzyciela,
                      Tw17Data.statement,
                      true
                    )
                    naturalPersonData.add(
                      components.createRadioGroup(
                        state.form.zalacznikiMalzonek.admId,
                        "Identyfikator administracyjny",
                        Tw17Data.optionsOfOIdentityZalacznik,
                        identityMap
                      )
                    )
                    naturalPersonData.add(identityMap)
                    concersData["5"] = arrayListOf<Any>(
                      components.createFieldset("Małżonek odpowiedzialny majątkiem wspólnym"),
                      arrayListOf(
                        components.createInput(state.form.zalacznikiMalzonek.imie, "Imię małżonka"),
                        components.createInput(state.form.zalacznikiMalzonek.nazwisko, "Nazwisko małżonka"),
                          address.createAddress(
                            state.form.zalacznikiMalzonek.adres,
                            state.form.zalacznikiMalzonek.adresData
                          ),
                       naturalPersonData,
                        person.createInnyNrId(
                          state.form.zalacznikiMalzonek.nrIdentyfikacyjny,
                          state.form.zalacznikiMalzonek.posiadaNrId
                        )
                      )
                    )

                    /*val identityRodzajMap = HashMap<String, Any?>()
                    val rodzajData = ArrayList<Any>()

                    identityRodzajMap["0"] = arrayListOf<Any>(
                      person.createNaturalPersonZalacznik(state.form.zalacznikPodmitZobowiazany.osobaFizyczna)
                    )
                    identityRodzajMap["1"] = arrayListOf<Any>(
                      person.createNonPhysicalPersonZalacznik(state.form.zalacznikPodmitZobowiazany.osobaNiefizyczna)
                    )

                    rodzajData.add(
                      components.createRadioGroup(
                        state.form.zalacznikPodmitZobowiazany.rodzaj,
                        "Rodzaj podmiotu",
                        Tw17Data.optionsOfPrimalObligated,
                        identityRodzajMap
                      )
                    )
                    rodzajData.add(identityRodzajMap)*/
                    val rodzajData = ArrayList<Any>()
                    rodzajData.addAll(
                      person.createPositionsZalacznikPodmiot(state.form.zalacznikPodmitZobowiazany)
                    )
                    concersData["6"] = arrayListOf<Any>(
                      components.createFieldset("Podmiot niebędący zobowiązanym, na który zostało przeniesione prawo własności rzeczy lub inne prawo majątkowe obciążone zastawem skarbowym lub hipoteką przymusową"),
                      rodzajData
                    )

                    /*val identityRodzajPodmiotMap = HashMap<String, Any?>()
                    val rodzajDataPodmiot = ArrayList<Any>()

                    identityRodzajPodmiotMap["0"] = arrayListOf<Any>(
                      person.createNaturalPersonZalacznik(state.form.zalacznikPodmiotMajatkowy.osobaFizyczna)
                    )
                    identityRodzajPodmiotMap["1"] = arrayListOf<Any>(
                      person.createNonPhysicalPersonZalacznik(state.form.zalacznikPodmiotMajatkowy.osobaNiefizyczna)
                    )

                    rodzajDataPodmiot.add(
                      components.createRadioGroup(
                        state.form.zalacznikPodmiotMajatkowy.rodzaj,
                        "Rodzaj podmiotu",
                        Tw17Data.optionsOfPrimalObligated,
                        identityRodzajPodmiotMap
                      )
                    )
                    rodzajDataPodmiot.add(identityRodzajPodmiotMap)

                    val majatekLabel = "Składnik majątku, do którego może być skierowana egzekucja"
                    val firstMajetek = person.createAnAsset(state.form.skladnikMajatku, majatekLabel)
                    val div2 = Div() { id = "anAsset2" }
                    rodzajDataPodmiot.add(div2)
                    rodzajDataPodmiot.add(
                      arrayListOf(
                        firstMajetek,
                        person.createButtonAssetGroup(
                          state.form.zalacznikPodmiotMajatkowy.majatekList,
                          div2,
                          majatekLabel
                        )
                      )
                    )*/
                    val rodzajDataPodmiot = ArrayList<Any>()
                    rodzajDataPodmiot.addAll(
                      person.createPositionsZalacznikPodmiotMajatek(state.form.zalacznikPodmiotMajatkowy)
                    )
                    concersData["7"] = arrayListOf<Any>(
                      components.createFieldset("Podmiot, który uzyskał korzyść majątkową"),
                      rodzajDataPodmiot
                    )

                    concersData["8"] = arrayListOf<Any>(
                      components.createFieldset("Imienia i nazwiska oraz adresu do korespondencji przedstawiciela ustawowego/opiekuna/kuratora zobowiązanego"),
                      arrayListOf(
                        components.createSelect(
                          state.form.reprezentantTypPrzedstawiciela,
                          "Typ przedstawiciela",
                          Tw17Data.listOfRepresentativeTypes
                        ),
                        components.createInput(state.form.reprezentantImie, "Imię"),
                        components.createInput(state.form.reprezentantNazwisko, "Nazwisko"),
                        address.createAddress(
                          state.form.reprezentantAdres,
                          state.form.reprezentantAdresData
                        ),
                      )
                    )
                    concersData["9"] = arrayListOf<Any>(
                      components.createFieldset("Imienia i nazwiska oraz adresu do korespondencji zarządcy przedsiębiorstwa w spadku"),
                      arrayListOf(
                        components.createInput(state.form.zarzadcaWSpadkuImie, "Imię"),
                        components.createInput(state.form.zarzadcaWSpadkuNazwisko, "Nazwisko"),
                        address.createAddress(
                          state.form.zarzadcaWSpadkuAdres,
                          state.form.zarzadcaWSpadkuAdresData
                        ),
                        components.createFieldset("Dane zmarłego przedsiębiorcy"),
                        arrayListOf(
                          components.createInput(
                            state.form.daneZmarlegoPrzedsiebiorcyImie,
                            "Imię zmarłego przedsiębiorcy"
                          ),
                          components.createInput(
                            state.form.daneZmarlegoPrzedsiebiorcyNazwisko,
                            "Nazwisko zmarłego przedsiębiorcy"
                          ),
                          components.createInput(
                            state.form.daneZmarlegoPrzedsiebiorcyPesel,
                            "Numer ewidencyjny PESEL zmarłego przedsiębiorcy",
                            requirements = FormReadingUtils.checkValidation("TNrPESEL")
                          )
                        )
                      )
                    )
                    val otherLabel = "Inna okoliczność"
                    val otherCircumstances = person.createAnAsset(state.form.innaOkolicznosc, otherLabel)
                    val other = Div() { id = "enforcementAuthority " }
                    concersData["10"] = arrayListOf<Any>(
                      components.createFieldset("Innych okoliczności niewskazanych w ustawie"),
                      other,
                      arrayListOf(
                        otherCircumstances,
                        person.createButtonAssetGroup(state.form.innaOkolicznosc, other, otherLabel)
                      )
                    )
                    val radio = components.createRadioGroup(
                      state.form.zakresInfomacji,
                      "Informacja dotyczy:",
                      Tw17Data.optionsOfConcers,
                      concersData,
                      isRequired = false
                    )
                    add(radio.field)
                    components.readComponentsList(concersData, this, radio)
                  }
                }
                add(formComponents.createNavigationButtons())
                //console.log("namespaceURI: " + state.form.namespaceURI)
              }
            }
          }
          else -> {
            // Block
          }
        }
      }
    }
  }
}
